import { useState } from "react";
import { FaSpinner } from "react-icons/fa6";
import { BuildingLibraryIcon } from "@heroicons/react/24/outline";

import {
  useGetVirtualAccounts,
  useGetVirtualAccountBanks,
  useCreateVirtualAccount,
  virtualAccountsResourcePath,
} from "../../../../services/queries/virtual-accounts";
import { showToast } from "../../../../utils/toast";
import { parseError } from "../../../../utils";
import { logError } from "../../../../utils/sentry";
import BankAccountDetails from "../../../BankAccountDetails";
import EmptyAccountState from "../../../EmptyAccountState";
import InfoBox from "../../../InfoBox";
import Loader from "../../../Loader";

interface BankTransferPanelProps {
  currency: string | null;
  buildApiUrl: (url: string) => string;
}

interface BankOption {
  value: string;
  label: string;
}

function GeneratingCard() {
  return (
    <div className="flex flex-col items-center py-6 text-center">
      <FaSpinner className="h-6 w-6 animate-spin text-primary" />
      <p className="mt-3 text-sm font-medium text-gray-700">
        Generating your account…
      </p>
      <p className="mt-1 text-xs text-gray-400">
        This usually takes a few seconds.
      </p>
    </div>
  );
}

interface GenerateAccountFormProps {
  banks: BankOption[];
  activeBank: string;
  isCreating: boolean;
  submitLabel: string;
  onSelectBank: (value: string) => void;
  onGenerate: () => void;
}

function GenerateAccountForm({
  banks,
  activeBank,
  isCreating,
  submitLabel,
  onSelectBank,
  onGenerate,
}: GenerateAccountFormProps) {
  return (
    <div className="space-y-4 rounded-xl border border-gray-100 p-4">
      {banks.length > 1 && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-700">
            Preferred bank
          </p>
          <div className="grid grid-cols-2 gap-2">
            {banks.map((bank) => {
              const isSelected = bank.value === activeBank;
              return (
                <button
                  key={bank.value}
                  type="button"
                  onClick={() => onSelectBank(bank.value)}
                  className={`rounded-xl border p-3 text-sm font-medium transition-colors ${
                    isSelected
                      ? "border-primary-200 bg-secondary-200 text-primary"
                      : "border-primary-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {bank.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onGenerate}
        disabled={isCreating || !activeBank}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
      >
        {isCreating && <FaSpinner className="h-4 w-4 animate-spin" />}
        {isCreating ? "Generating…" : submitLabel}
      </button>
    </div>
  );
}

export default function BankTransferPanel({
  currency,
  buildApiUrl,
}: BankTransferPanelProps) {
  const virtualAccountsUrl = buildApiUrl(
    `${virtualAccountsResourcePath}?currency=${currency}`,
  );
  const { data, isLoading } = useGetVirtualAccounts(virtualAccountsUrl);
  const accounts = data?.data?.list ?? [];

  // Only fully-provisioned accounts are treated as real. Incomplete rows the
  // server may return (a stale/never-finished provisioning) are ignored so
  // they can't leave the panel stuck on a spinner the user never triggered.
  const readyAccounts = accounts.filter(
    (account) => account.accountName && account.accountNumber,
  );
  const hasReadyAccount = (bankValue: string) =>
    readyAccounts.some((account) => account.bankCode === bankValue);

  // Banks the user has submitted a create for during this session. The
  // "Generating…" state is driven by this local intent — not by server data —
  // so it only appears after the user acts, and clears on remount.
  const [pendingBanks, setPendingBanks] = useState<string[]>([]);
  const generatingBanks = pendingBanks.filter((bank) => !hasReadyAccount(bank));

  // Banks that support virtual accounts come from the API (cached), so a
  // stale/renamed provider slug can't break account creation.
  const { banks: bankOptions, isLoading: isLoadingBanks } =
    useGetVirtualAccountBanks();
  const banks = bankOptions.map((bank) => ({
    value: bank.code,
    label: bank.name,
  }));
  // A bank can open a new account only if it has neither a ready account nor
  // an in-flight one — letting a user hold one account per bank without
  // double-submitting.
  const availableBanks = banks.filter(
    (bank) =>
      !hasReadyAccount(bank.value) && !generatingBanks.includes(bank.value),
  );

  const [selectedBank, setSelectedBank] = useState("");
  const activeBank =
    availableBanks.find((bank) => bank.value === selectedBank)?.value ??
    availableBanks[0]?.value ??
    "";

  const { mutate: createAccount, isLoading: isCreating } =
    useCreateVirtualAccount();

  const handleGenerate = () => {
    if (!activeBank) return;
    const bank = activeBank;
    createAccount(
      { data: { bankCode: bank, currency } },
      {
        onSuccess: () => {
          setPendingBanks((prev) =>
            prev.includes(bank) ? prev : [...prev, bank],
          );
          showToast("Virtual account creation in progress...", "info");
        },
        onError: (error: unknown) => {
          logError(error);
          showToast(parseError(error), "error");
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div
        data-testid="bank-transfer-loading"
        className="flex items-center justify-center py-12"
      >
        <FaSpinner className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const hasAccountsToShow =
    readyAccounts.length > 0 || generatingBanks.length > 0;

  return (
    <div className="space-y-4">
      {readyAccounts.length > 0 && (
        <>
          <InfoBox infoText="Transfer any amount to these account details. Your wallet is credited automatically once the transfer lands." />

          {readyAccounts.map((account) => (
            <div
              key={account.id}
              className="rounded-xl border border-gray-100 p-4"
            >
              <BankAccountDetails
                bankName={account.bankName}
                accountName={account.accountName}
                accountNumber={account.accountNumber}
              />
            </div>
          ))}
        </>
      )}

      {generatingBanks.map((bank) => (
        <div key={bank} className="rounded-xl border border-gray-100 p-4">
          <GeneratingCard />
        </div>
      ))}

      {!hasAccountsToShow && (
        <EmptyAccountState
          icon={
            <BuildingLibraryIcon className="h-16 w-16 text-gray-300 my-4" />
          }
          title="No virtual accounts found"
          body="Generate a dedicated account number to fund your wallet by bank transfer."
        />
      )}

      {isLoadingBanks ? (
        <Loader className="rounded-xl border border-gray-100 p-6" />
      ) : availableBanks.length > 0 ? (
        <GenerateAccountForm
          banks={availableBanks}
          activeBank={activeBank}
          isCreating={isCreating}
          submitLabel={
            hasAccountsToShow
              ? "Add another account"
              : "Generate account number"
          }
          onSelectBank={setSelectedBank}
          onGenerate={handleGenerate}
        />
      ) : null}
    </div>
  );
}
